/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/ConvertServiceEndpointToUseSecretDialog" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { TitleSize } from "VSSUI/Header";
import { ServiceEndpoint, ServiceEndpointAuthenticationScheme } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import { IVssPageContext } from "VSS/Platform/Context";
import { Link } from "VSSUI/Link";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import {
    IServiceEndpointComponentState,
    ServiceEndpointStore
} from "DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointStore";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import * as Utils_String from "VSS/Core/Util/String";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export interface IConvertServiceEndpointToUseSecretDialogProps {
    primaryOnClick: () => void;
    onDismiss: () => void;
    onBackNavigation: () => void;
    serviceEndpoint: ServiceEndpoint;
}

export interface IServiceEndpointToUseSecretDialogState extends IServiceEndpointComponentState {
    secret?: string;
    isSaving: boolean;
    savingFailed: boolean;
    endpointScheme?: ServiceEndpointAuthenticationScheme;
}

export class ConvertServiceEndpointToUseSecretDialog extends VssComponent<IConvertServiceEndpointToUseSecretDialogProps, IServiceEndpointToUseSecretDialogState> {
    constructor(props: IConvertServiceEndpointToUseSecretDialogProps, context: IVssComponentContext) {
        super(props, context);
        this._actionCreator = ActionCreatorManager.CreateActionCreator<ServiceEndpointActionsCreator, IVssPageContext>(ServiceEndpointActionsCreator, "service-endpoints-section-actionscreator", this.context.pageContext);
        this.state = {
            ...ServiceEndpointStore.getDefaultState(),
            secret: null,
            endpointScheme: null,
            isSaving: false,
            savingFailed: false
        };
        this._store = StoreManager.GetStore<ServiceEndpointStore>(ServiceEndpointStore, ServiceEndpointStore.getKey());
    }

    public componentWillMount(): void {
        this._onStoreChanged();
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
    }

    private _onStoreChanged = (): void => {
        const state = this._store.getState();
        this.setState({...state, endpointScheme: this._getScheme(state)});
    }

    private _getScheme = (state: IServiceEndpointComponentState): ServiceEndpointAuthenticationScheme =>
        state.serviceEndpointType.authenticationSchemes.find(x => x.scheme === EndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal)

    public render(): React.ReactNode {
        if (!this.state.serviceEndpointType) {
            return null;
        }

        const isDirty = this.state.secret !== null;
        const isError =  isDirty && this.state.secret.length === 0;
        const isSaving = this.state.isSaving;

        return (
            <Dialog
                titleProps={{ text: Resources.ConvertARMEndpointToUseSecretMessage, size: TitleSize.Large }}
                onDismiss={() => {
                    this.props.onDismiss();
                }}
                footerButtonProps={[
                    {
                        text: Resources.Cancel,
                        disabled: isSaving,
                        onClick: () => {
                            this.props.onDismiss();
                        }
                    },
                    {
                        text: Resources.Convert,
                        primary: true,
                        disabled: !isDirty || isError || isSaving,
                        onClick: () => {
                            this.setTimeout(() => this.setState({isSaving: true}));
                            this._actionCreator.convertServiceEndpointAuthenticationSchemeToSecret(
                                this.state.serviceEndpoint,
                                this.state.endpointScheme,
                                { [EndpointConstants.AzureEndpointConstants.SpnKey.toLowerCase()]: this.state.secret }
                            ).then(() => this.props.primaryOnClick()).catch(_ => this.setTimeout(() => this.setState({isSaving: false, savingFailed: true})));
                        }
                    }
                ]}

            >
                <div>
                    <div className="margin-bottom-8">
                    <span>{Resources.ConvertARMEndpointToUseSecretDialogMessage}</span>&nbsp;
                        <Link target="_blank" ariaLabel={Resources.LearnMoreText} href="https:/aka.ms/azdo-rm-workload-identity-conversion">{Resources.LearnMoreText}</Link>
                    </div>

                    {this.state.isSaving
                        ? <Spinner
                            ariaLive="assertive"
                            label={Resources.ConvertARMEndpointToUseSecretSavingMessage}
                            className="loading-spinner"
                            size={SpinnerSize.medium} />
                        : <FormItem
                            label={Resources.ConvertARMEndpointToUseSecretFieldTitle}
                            className="default-input-margin-top"
                            error={isError}
                            message={isError ? Resources.ConvertARMEndpointToUseSecretFieldRequiredMessage : ""}>
                            <TextField
                                value={this.state.secret}
                                inputType="password"
                                autoComplete={false}
                                autoFocus={true}
                                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
                                    this.setState({ secret: value });
                                }}
                            />
                        </FormItem>}

                     { this.state.savingFailed &&
                     <MessageCard className="flex-self-stretch  margin-top-16 margin-bottom-16" severity={MessageCardSeverity.Error}>
                        {Resources.ConvertARMEndpointToUseSecretFailedMessage}
                    </MessageCard>}
                </div>
            </Dialog>
        );
    }

    private readonly _store: ServiceEndpointStore;
    private readonly _actionCreator: ServiceEndpointActionsCreator;
}

VssComponent.register("service-endpoint-convert-to-use-secret", ConvertServiceEndpointToUseSecretDialog);
